import { ColumnDef } from '@tanstack/react-table'
import { PencilIcon, MoreHorizontal, Eye } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import DeleteEventModal from '../DeleteEventModal'
import { IsActiveEvent } from './components/IsActiveEvent'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	getEventTypeLabel,
	getEventStatusLabel,
	EVENT_STATUS_BADGE_VARIANT,
} from '../../constants'
import { formatCurrency } from '@/common/lib/utils'
import type { UnifiedEventData } from '@/modules/events/type'

export const columns: ColumnDef<UnifiedEventData>[] = [
	{
		accessorKey: 'eventType',
		header: 'ประเภท',
		cell: ({ row }) => (
			<Badge variant="outline">
				{getEventTypeLabel(row.original.eventType)}
			</Badge>
		),
	},
	{
		accessorKey: 'title',
		header: 'ชื่อกิจกรรม',
		cell: ({ row }) => (
			<div className="max-w-[250px]">
				<div className="font-medium">{row.original.title}</div>
			</div>
		),
	},
	{
		accessorKey: 'bannerUrl',
		header: 'แบนเนอร์',
		cell: ({ row }) => {
			if (!row.original.bannerUrl) return '-'
			return (
				<div className="h-12 w-20 overflow-hidden rounded">
					<img
						src={row.original.bannerUrl}
						alt="Banner"
						className="h-full w-full object-cover"
					/>
				</div>
			)
		},
	},
	{
		id: 'eventDetails',
		header: 'รายละเอียด',
		cell: ({ row }) => {
			if (row.original.eventType === 'CUSTOM') {
				return (
					<div className="max-w-[200px] truncate text-sm text-muted-foreground">
						{row.original.shortDescription || '-'}
					</div>
				)
			} else {
				// Turnover event details
				return (
					<div className="space-y-1 text-sm">
						<div>
							ยอด: {formatCurrency(Number(row.original.requiredTurnover))}
						</div>
						<div>
							รางวัล: {formatCurrency(Number(row.original.rewardAmount))}
						</div>
					</div>
				)
			}
		},
	},
	{
		accessorKey: 'status',
		header: 'สถานะ',
		cell: ({ row }) => {
			if (row.original.eventType === 'CUSTOM') {
				return <IsActiveEvent row={row.original} />
			} else {
				const status = row.original.status
				return (
					<Badge variant={EVENT_STATUS_BADGE_VARIANT[status]}>
						{getEventStatusLabel(status)}
					</Badge>
				)
			}
		},
	},
	{
		id: 'dates',
		header: 'ช่วงเวลา',
		cell: ({ row }) => {
			if (row.original.eventType === 'CUSTOM') {
				return '-'
			} else {
				return (
					<div className="space-y-1 text-sm">
						<div>
							{format(new Date(row.original.startDate), 'dd MMM yyyy', {
								locale: th,
							})}
						</div>
						<div className="text-muted-foreground">
							-{' '}
							{format(new Date(row.original.endDate), 'dd MMM yyyy', {
								locale: th,
							})}
						</div>
					</div>
				)
			}
		},
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่สร้าง',
		cell: ({ row }) =>
			format(new Date(row.original.createdAt), 'dd MMM yyyy HH:mm', {
				locale: th,
			}),
	},
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => {
			if (row.original.eventType === 'CUSTOM') {
				return (
					<div className="flex items-center gap-2">
						<Button asChild variant="ghost" size="sm">
							<Link to={`/operator/events/${row.original.id}?type=CUSTOM`}>
								<PencilIcon className="h-4 w-4" />
							</Link>
						</Button>
						<DeleteEventModal
							eventId={row.original.id}
							eventTitle={row.original.title}
						/>
					</div>
				)
			} else {
				return (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm">
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem asChild>
								<Link to={`/operator/events/${row.original.id}?type=TURNOVER`}>
									<PencilIcon className="mr-2 h-4 w-4" />
									แก้ไข
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem asChild>
								<Link to={`/operator/events/${row.original.id}/claims`}>
									<Eye className="mr-2 h-4 w-4" />
									ดูการเคลม
								</Link>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)
			}
		},
	},
]

export const writeAccessorKeys = ['actions']
